<template>
  <div class="icu-dz-wrapper">
    <modal-wrapper v-if="showDeleteFileModal">
      <modal-container :show-close="false">
        <img
          src="@/assets/img/logo.svg"
          class="register-page__modal-logo"
          width="100"
          height="40"
          alt="ICU"
        />
        <div class="register-page__modal-heading">
          {{ $t('notifications.delete-photo-message') }}
        </div>
        <div class="register-page__modal-buttons">
          <pc-button class="w100" variant="dark" @click="deleteFile">
            {{ $t('notifications.delete-photo-yes') }}
          </pc-button>
          <pc-button
            variant="void"
            class="w100"
            @click="showDeleteFileModal = false"
          >
            {{ $t('notifications.delete-photo-no') }}
          </pc-button>
        </div>
      </modal-container>
    </modal-wrapper>

    <input
      ref="input"
      type="file"
      class="icu-dz__input"
      :accept="getAcceptable()"
      @input="emitFiles"
    />
    <input-label
      v-if="label"
      class="icu-dz__label"
      :disabled="disabled"
    >
      {{ fieldLabel }}
      <v-tooltip placement="bottom" theme="dropzone-tooltip">
        <div
          v-if="hint"
          class="icu-dz-hint"
        />

        <template #popper>
          <div v-html="hint" />
        </template>
      </v-tooltip>
    </input-label>
    <div class="icu-dz">
      <div
        v-for="file in value"
        :key="file.id"
        class="icu-dz-item"
      >
        <div v-if="file.newAdded" class="icu-dz-item__close" @click="displayDeleteFileModal(file)">
          <img class="icu-dz-item__close-icon" src="@/assets/img/views/RegisterPage/delete-file-icon.svg" alt="delete" />
        </div>
        <div v-if="file.src" class="icu-dz-item-img" :style="{backgroundImage: `url(${file.src})`}"></div>
        <div v-else :class="{'is-blurred': file.isUploading || file.hasError}" class="icu-dz-item-info">
          <div class="icu-dz-item-info-format">
            <icu-icon
              :icon-name="isFileImage(file.name || file.doc) ? 'photo' : 'pdf'"
              class="icu-dz-item-info-format__icon"
            />
            {{ getFileExtension(file.name || file.doc) }}
          </div>
          <div class="icu-dz-item-info__filename">
            <template v-if="isMobile">
              {{ getMobileFileName(file.name || file.doc) }}
            </template>
            <template v-else>
              {{ file.name || file.doc }}
            </template>
          </div>
        </div>
        <div class="icu-dz-item-status">
          <div v-if="file.newAdded">
            <div v-if="file.error && file.error.length" class="icu-dz-item-status__text not-uploaded">
              {{ file.error[0] }}
            </div>
          </div>
          <div v-else-if="file.isUploading" class="icu-dz-item-status__text is-uploading">
            {{ file.uploadPercent }}% Завантаження
          </div>
          <template v-else-if="file.hasError">
            <icu-icon icon-name="cross" class="icu-dz-item-status__icon not-uploaded" />
            <div class="icu-dz-item-status__text not-uploaded">
              {{ $t('common.upload-failed') }}
            </div>
          </template>
          <template v-else>
            <icu-icon icon-name="added" class="icu-dz-item-status__icon" />
            <div class="icu-dz-item-status__text">
              {{ $t('common.upload-successful') }}
            </div>
          </template>
        </div>
      </div>

      <div
        v-if="canUploadMore"
        class="icu-dz-new-wrapper"
        :class="{'part-size': value.length, 'hovered': isFileBeingDropped}"
        @click="openUploadWindow"
        @drop.prevent="fileDrop"
        @dragover.prevent="isFileBeingDropped = true"
        @dragleave="isFileBeingDropped = false"
      >
        <div class="icu-dz-new">
          <icu-icon icon-name="plus" class="icu-dz-new__add-icon" />
          <div class="icu-dz-new-top">
            <div class="icu-dz-new-top__title">
              <icu-icon class="icu-dz-new-top__icon" icon-name="photo" />
              <span v-html="$t(title)"></span>
            </div>
          </div>
          <div class="icu-dz-new__subtext">
            <div>{{ $t(subtext) }}</div>
            <div v-if="!isResident">
              {{ $t('common.drag-document-file-extensions') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <input-error v-if="errorMessage && !isAnyFileUploading" class="icu-dz__error">
      {{ $t(errorMessage) }}
    </input-error>
  </div>
</template>

<script>
import InputLabel from '@/components/inputs/InputLabel.vue'
import IcuIcon from '@/components/common/IcuIcon.vue'
import InputError from '@/components/inputs/InputError.vue'
import { constants } from '@/constants.js'
import ModalWrapper from '@/components/modals/ModalWrapper'
import ModalContainer from '@/components/modals/ModalContainer'
import PcButton from '@/components/common/PcButton.vue'

export default {
  name: 'IcuDropzone',
  components: {
    PcButton,
    ModalContainer,
    ModalWrapper,
    InputError,
    IcuIcon,
    InputLabel
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: 'common.add-photo-scan-copy'
    },
    subtext: {
      type: String,
      default: 'common.drag-document-or-attach',
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: () => []
    },
    maximumFiles: {
      type: Number,
      default: 1
    },
    infiniteUpload: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    },
    isResident: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      isMobile: false,
      isFileBeingDropped: false,
      showDeleteFileModal: false,
      fileToDelete: null
    }
  },
  computed: {
    imgExt() {
      return this.isResident ? 'RESIDENT_IMAGE_EXTENSIONS' : 'NONRESIDENT_IMAGE_EXTENSIONS'
    },
    fieldLabel() {
      if (this.required) {
        return `${this.label} *`
      }

      return this.label
    },
    errorMessage() {
      return this.errors?.[0] || ''
    },
    canUploadMore() {
      if (this.infiniteUpload) {
        return true
      }

      return this.value.length < this.maximumFiles
    },
    isAnyFileUploading() {
      return !!this.value.find(file => file.isUploading)
    },
  },
  watch: {
    isAnyFileUploading(newVal, oldVal) {
      if (newVal === false && oldVal) {
        this.$emit('files-uploaded')
      }
    }
  },
  mounted() {
   this.checkIsMobile()
   window.addEventListener('resize', this.checkIsMobile)
  },
  destroyed() {
    window.removeEventListener('resize', this.checkIsMobile)
  },
  methods: {
    checkIsMobile(){
      this.isMobile = window.innerWidth <= 600
    },
    getAcceptable(){
      return constants[this.imgExt].map(type => type==='pdf'? 'application/pdf': `image/${type}`)
    },
    openUploadWindow() {
      this.$refs.input.click()
    },
    emitFiles(event) {
      if (event.target.files?.length) {
        this.emitInput(event.target.files[0])
        this.$refs.input.value = null
      }
    },
    displayDeleteFileModal(file) {
      this.showDeleteFileModal = true
      this.fileToDelete = file

    },
    deleteFile(){
      this.$emit('delete-file', this.fileToDelete)
      this.showDeleteFileModal = false
      this.fileToDelete = null
    },
    emitInput(file) {
      if( file.size >= 104857600) {
        this.$toast.error(this.$t('common.file-too-large'))
        return
      }
      if (this.canUploadMore) {
        const fileExtension = this.getFileExtension(file.name)

        if (!constants[this.imgExt].includes(fileExtension)) {
          this.$toast.error(this.$t(`common.unacceptable-file-format${!this.isResident? '-nonresident': ''}`))
          return
        }

        this.$emit('input', file)
      }
    },
    isFileImage(fileName) {
      const fileExtension = this.getFileExtension(fileName)

      return constants[this.imgExt].includes(fileExtension)
    },
    getMobileFileName(fileName) {
        return fileName.substring(0,24)+ '...'
    },
    getFileExtension(fileName) {
      return fileName.split('.').pop().toLowerCase()
    },
    fileDrop(event) {
      if (event.dataTransfer.items.length) {
        this.emitInput(event.dataTransfer.items[0].getAsFile())
      }
    }
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/components/inputs/IcuDropzone.scss";
.register-page__modal-heading {
  padding: 0;
  margin: 0;
}
 .w100 {
   width: 100%;
 }
</style>
